//! Holds the column headers for a table

import type { Table } from '@/app/gridGL/cells/tables/Table';
import { TableColumnHeader } from '@/app/gridGL/cells/tables/TableColumnHeader';
import type { TablePointerDownResult } from '@/app/gridGL/cells/tables/Tables';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import { FILL_SELECTION_ALPHA } from '@/app/gridGL/UI/Cursor';
import { getCSSVariableTint } from '@/app/helpers/convertColor';
import type { JsCoordinate } from '@/app/quadratic-core-types';
import { colors } from '@/app/theme/colors';
import { sharedEvents } from '@/shared/sharedEvents';
import type { Point } from 'pixi.js';
import { Container, Graphics, Rectangle } from 'pixi.js';

export class TableColumnHeaders extends Container {
  private table: Table;
  private background: Graphics;
  private columnsHeight = 0;

  columns: Container<TableColumnHeader>;
  tableCursor: string | undefined;

  constructor(table: Table) {
    super();
    this.table = table;
    this.background = this.addChild(new Graphics());
    this.columns = this.addChild(new Container<TableColumnHeader>());

    sharedEvents.on('changeThemeAccentColor', this.drawBackground);
    sharedEvents.on('changeThemeAppearanceMode', this.update);
  }

  destroy() {
    sharedEvents.off('changeThemeAccentColor', this.drawBackground);
    sharedEvents.off('changeThemeAppearanceMode', this.update);
    super.destroy();
  }

  toggleTableColumnSelection(hide: boolean) {
    this.drawBackground(hide);
  }

  drawBackground = (skipSelection = false) => {
    this.background.clear();

    this.background.lineStyle();
    this.background.beginFill(colors.gridBackground);

    // need to adjust so the outside border is still visible
    this.background.drawShape(new Rectangle(0.5, 0, this.table.tableBounds.width - 1, this.columnsHeight));
    this.background.endFill();

    // draw borders on the top and bottom of the column headers (either active or inactive)
    const active = content.cellsSheet.tables.isActive(this.table);
    if (this.table.inOverHeadings && active) {
      const width = active ? 1 : 0;
      this.background.lineStyle({
        color: getCSSVariableTint(this.table.active ? 'primary' : 'muted-foreground'),
        width,
        alignment: 1,
      });
      this.background.moveTo(0, 0);
      this.background.lineTo(0, this.columnsHeight);
      this.background.lineStyle({
        color: getCSSVariableTint(this.table.active ? 'primary' : 'muted-foreground'),
        width,
        alignment: 0,
      });
      this.background.moveTo(this.table.tableBounds.width, 0);
      this.background.lineTo(this.table.tableBounds.width, this.columnsHeight);
    }

    if (!skipSelection) {
      // draws selection background
      const columnsSelected = this.table.sheet.cursor.getTableColumnSelection(this.table.codeCell.name);
      if (columnsSelected) {
        const startX = this.table.sheet.offsets.getColumnPlacement(
          this.table.codeCell.x + columnsSelected[0]
        )?.position;
        const end = this.table.sheet.offsets.getColumnPlacement(
          this.table.codeCell.x + columnsSelected[columnsSelected.length - 1]
        );
        const endX = end.position + end.size;
        this.background.lineStyle();
        this.background.beginFill(content.accentColor, FILL_SELECTION_ALPHA);
        this.background.drawRect(startX - this.table.tableBounds.x, 0, endX - startX, this.columnsHeight);
        this.background.endFill();
      }
    }

    this.background.lineStyle({
      color: getCSSVariableTint(this.table.active ? 'primary' : 'muted-foreground'),
      width: 1,
      alignment: 1,
    });
    this.background.moveTo(0, this.columnsHeight);
    this.background.lineTo(this.table.tableBounds.width, this.columnsHeight);
  };

  private createColumnHeaders = () => {
    this.columns.removeChildren();

    const codeCell = this.table.codeCell;

    if (!codeCell.show_columns) {
      this.columns.visible = false;
      return;
    }

    this.columns.visible = true;

    const columnY = codeCell.show_name ? this.table.sheet.offsets.getRowHeight(codeCell.y) : 0;

    let displayX = 0;

    codeCell.columns
      .filter((c) => c.display)
      .forEach((column, displayIndex) => {
        const width = this.table.sheet.offsets.getColumnWidth(codeCell.x + displayIndex);

        this.columns.addChild(
          new TableColumnHeader({
            table: this.table,
            index: column.valueIndex,
            x: displayX,
            width,
            height: this.columnsHeight,
            name: column.name,
            sort: codeCell.sort?.find((s) => s.column_index === column.valueIndex),
            filtered: codeCell.filtered_columns.includes(column.valueIndex),
            dirty: codeCell.sort_dirty,
            columnY,
          })
        );

        displayX += width;
      });
  };

  // update appearance when there is an updated code cell
  update = () => {
    const codeCell = this.table.codeCell;
    if (codeCell.show_columns) {
      this.visible = true;
      this.columnsHeight = this.table.sheet.offsets.getRowHeight(codeCell.y + (codeCell.show_name ? 1 : 0));
      this.drawBackground();
      this.createColumnHeaders();
    } else {
      this.visible = false;
    }
  };

  pointerMove(world: Point): boolean {
    const adjustedWorld = world.clone();
    const found = this.columns.children.find((column) => column.pointerMove(adjustedWorld));
    if (!found) {
      this.tableCursor = undefined;
    } else {
      this.tableCursor = found.tableCursor;
    }
    return !!found;
  }

  pointerDown(world: Point, isRightClick: boolean): TablePointerDownResult | undefined {
    for (const column of this.columns.children) {
      const result = column.pointerDown(world, isRightClick);
      if (result) {
        return result;
      }
    }
  }

  // Returns undefined when `index` has no header right now. `createColumnHeaders`
  // rebuilds `columns.children` from scratch (removeChildren, then one child per
  // `display` column) on every table update, so a valueIndex captured earlier can
  // stop resolving whenever the table's columns change underneath us — a
  // collaborator or AI hiding/removing a column (`updateCodeCells`), or a
  // `sheetOffsets` rebuild. Every caller above already declares and handles
  // `Rectangle | undefined` — `TableHeader`/`Table`/`Tables.getTableColumnHeaderPosition`
  // all propagate it, `getColumnHeaderCell` guards with `if (bounds)`, and
  // `TableColumnHeaderRename` re-resolves on every `viewportReadyAfterUpdate` with
  // `bounds?.width ?? 0` / `if (!bounds) return`. Throwing here made all of those
  // guards unreachable.
  getColumnHeaderBounds(index: number): Rectangle | undefined {
    return this.columns.children.find((c) => c.index === index)?.columnHeaderBounds;
  }

  // Hides a column header
  hide(index: number) {
    const columnHeader = this.columns.children.find((c) => c.index === index);
    if (!columnHeader) {
      // Same transient mismatch as getColumnHeaderBounds above. This is a
      // decoration-only mutation (it blanks the pixi header so the HTML rename
      // input can sit on top), and it runs from `Tables.contextMenu` inside an
      // un-awaited async pointer handler (`PointerTable.pointerDownColumnName`),
      // so throwing surfaced as an unhandled rejection that aborted the rest of
      // the pointerdown — remaining `contextMenu` listeners and
      // `Pointer.updateCursor` never ran (INTERNAL-72). Skipping instead costs at
      // most one frame of the header drawn behind the rename input, and the next
      // `update()` re-syncs it. `show()` below is already tolerant this way.
      console.warn(`[TableColumnHeaders] no column header for index ${index}; skipping hide`);
      return;
    }
    columnHeader.visible = false;
  }

  // Shows all column headers
  show() {
    this.columns.children.forEach((column) => (column.visible = true));
  }

  getSortDialogPosition(): JsCoordinate | undefined {
    if (this.columns.children.length === 0) return;
    const firstColumn = this.columns.children[0];
    return { x: firstColumn.columnHeaderBounds.left, y: firstColumn.columnHeaderBounds.bottom + this.y };
  }

  getColumnHeaderLines(): { y0: number; y1: number; lines: number[] } {
    const lines: number[] = [];
    this.columns.children.forEach((column, index) => {
      lines.push(column.x);
      if (index === this.columns.children.length - 1) {
        lines.push(column.x + column.w);
      }
    });
    return { y0: 0, y1: this.columnsHeight, lines };
  }

  toHoverGrid(y: number) {
    this.columns.children.forEach((column) => column.toHoverGrid(y));
    this.drawBackground();
  }
}
